import { MilestoneList } from '@/components/features/milestones/MilestoneList';
import { SubmitWorkAction } from '@/components/features/milestones/SubmitWorkAction';
import { useState } from 'react';
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { MainScreen } from '@/components/shared/MainScreen/MainScreen';
import { Button, Spinner } from '@/components/ui';
import {
  useProjectParticipants,
  useProjectView,
} from '@/hooks/useProjectViews';
import type {
  ProjectView,
  ProposalView,
  MilestoneView,
} from '@/types/current-project';
import { BackendError } from '@/api/adapter/client';
import { useAuthStore } from '@/stores/authStore';
import { PlanningControls } from './components/ProjectPlanning';
import { MilestoneTasks } from './components/MilestoneTasks';
import {
  MilestoneCompletion,
  OpenDisputeAction,
  ProjectCommandFeedback,
} from './components/MilestoneCompletion';
import { useProposalPresentation } from '@/hooks/useProposalReviews';
import { ProposalMilestones } from '@/pages/projects/components/BuildScope/ProposalMilestones';
import { ProposalReviewHistory } from '@/pages/projects/components/BuildScope/ProposalReviewHistory';
import { ProposalReviewActions } from './components/ProposalReviewActions';
import { useProjectCommand } from '@/hooks/useProjectCommand';
import { useDaoConfig } from '@/hooks/useDaoConfig';
import { ProjectHeader } from './components/ProjectHeader';
import { ProjectScopeInfo } from './components/ProjectScopeInfo';
import { ProjectTeamList } from './components/ProjectTeamList';
import { TasksKanbanView } from '@/pages/dashboard/components/TasksKanban';
import { ProjectPaymentSummary } from '@/pages/payments/components/PaymentComponents';

const tabs = [
  { id: 'tasks', label: 'Tasks' },
  { id: 'information', label: 'Information' },
  { id: 'payments', label: 'Payments' },
];
export default function ProjectDetailPage({
  reviewMode = false,
}: {
  reviewMode?: boolean;
}) {
  const { id } = useParams<{ id: string }>();
  const query = useProjectView(id);
  const principal = useAuthStore((state) => state.session?.principalId);
  const account = useAuthStore((state) => state.session?.accountId);
  const [params] = useSearchParams();
  if (query.isPending)
    return (
      <div role="status" className="p-8">
        <Spinner /> Loading project…
      </div>
    );
  if (query.isError)
    return (
      <div role="alert" className="space-y-4 p-8">
        <h1 className="text-xl font-semibold">
          {query.error instanceof BackendError && query.error.statusCode === 404
            ? 'Project not found or inaccessible'
            : 'Failed to load project'}
        </h1>
        <p>{query.error.message}</p>
        <Button onClick={() => void query.refetch()}>Retry</Button>
        <Link to="/projects" className="ml-4 text-primary">
          Back to Projects
        </Link>
      </div>
    );
  const submission = params.get('submission');
  const requestedMilestone = query.data.proposals
    .filter((p) => p.status === 'Approved')
    .flatMap((p) => p.milestones)
    .find((m) => m.submissions.some((s) => s.submissionId === submission));
  if (!reviewMode && account === query.data.client) {
    if (requestedMilestone)
      return (
        <Navigate
          replace
          to={
            '/projects/' +
            query.data.projectId +
            '/milestones/' +
            requestedMilestone.milestoneId +
            '/review'
          }
        />
      );
    if (params.get('review') === 'scope')
      return (
        <Navigate
          replace
          to={'/projects/' + query.data.projectId + '/review-scope'}
        />
      );
  }
  return (
    <ProjectDetailContent
      key={principal + ':' + query.data.projectId + ':' + (submission ?? '')}
      project={query.data}
      reviewMode={reviewMode}
      initialMilestoneId={requestedMilestone?.milestoneId}
    />
  );
}
function ProjectDetailContent({
  project,
  reviewMode,
  initialMilestoneId,
}: {
  project: ProjectView;
  reviewMode: boolean;
  initialMilestoneId?: string;
}) {
  const [selectedTab, setTab] = useState(
    initialMilestoneId ? 'tasks' : 'information'
  );
  const [selectedMilestoneId, setMilestone] = useState<string | null>(
    initialMilestoneId ?? null
  );
  const selectedMilestone = project.proposals
    .filter((p) => p.status === 'Approved')
    .flatMap((p) => p.milestones)
    .find((m) => m.milestoneId === selectedMilestoneId);
  const { coordinatorRole } = useDaoConfig();
  const account = useAuthStore((state) => state.session?.accountId);
  const role = useAuthStore((state) => state.user?.role);
  const navigate = useNavigate();
  const evaluationSaved = project.evaluations.some((e) => e.author === account);
  const canEvaluate =
    account === project.client ||
    account === project.coordinator ||
    project.proposals.some((p) =>
      p.milestones.some((m) => m.assignments.some((a) => a.worker === account))
    );
  const showTabs =
    !reviewMode &&
    role !== 'client' &&
    account !== project.client &&
    project.proposals.some((p) => p.status === 'Approved');
  const tab = showTabs ? selectedTab : 'information';
  const command = useProjectCommand(project.projectId);
  return (
    <MainScreen
      headerContent={
        <ProjectHeader
          project={project}
          onReviewMilestone={
            project.proposals.some((p) => p.status === 'Approved')
              ? () => {
                  setTab('tasks');
                  setMilestone(null);
                }
              : undefined
          }
        />
      }
      tabs={showTabs ? tabs : undefined}
      activeTab={tab}
      onTabChange={setTab}
      contentClassName={
        'space-y-6 px-4 pb-8 text-[--text-dark-primary] sm:px-14 ' +
        (tab === 'information' ? 'pt-16' : 'pt-8')
      }
    >
      {project.completed && (
        <div className="mt-6 flex flex-col justify-between gap-4 rounded-xl border border-[#3D3D3D] bg-[#231F1F] p-5 shadow-sm md:flex-row md:items-center">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#36D399]/20 text-[#36D399]">
              <i className="ri-checkbox-circle-fill text-xl" />
            </div>
            <div>
              <h4 className="text-base font-semibold text-[#F5F5F5]">
                The project is completed! It’s time to evaluate your teammates.
              </h4>
              <p className="text-xs text-[#9B9B9B]">
                {evaluationSaved
                  ? 'Your evaluation has been saved.'
                  : 'All milestones have been accepted and their payments released.'}
              </p>
            </div>
          </div>
          {canEvaluate && !evaluationSaved && (
            <div className="flex shrink-0 items-center gap-3">
              <Button
                variant="primary"
                onClick={() =>
                  navigate(
                    '/projects/' +
                      project.projectId +
                      '/evaluate' +
                      (account === project.client
                        ? ''
                        : account === project.coordinator
                          ? '/consultant'
                          : '/developer')
                  )
                }
              >
                {account === project.coordinator
                  ? 'Evaluate Teammates'
                  : `Evaluate Your ${coordinatorRole}`}
              </Button>
            </div>
          )}
        </div>
      )}
      {tab !== 'information' && <ProjectCommandFeedback command={command} />}
      {project.activeDisputeId && (
        <p className="mb-4 break-all text-red-300">
          The project is frozen by an open dispute.{' '}
          <Link
            className="text-primary underline"
            to={'/disputes/' + project.activeDisputeId}
          >
            View public dispute {project.activeDisputeId}
          </Link>
        </p>
      )}
      {tab === 'information' ? (
        <div className="space-y-8">
          <div
            className={
              reviewMode
                ? 'grid grid-cols-1 items-start gap-8 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]'
                : 'grid grid-cols-1 items-start gap-8 lg:grid-cols-2'
            }
          >
            <div
              className={
                reviewMode ? 'min-w-0 space-y-6 xl:order-2' : 'min-w-0'
              }
            >
              <ProjectScopeInfo project={project} />
              {reviewMode && (
                <PlanningControls project={project} command={command} />
              )}
            </div>
            <section
              className={
                reviewMode
                  ? 'min-w-0 space-y-4 text-left xl:order-1'
                  : 'min-w-0 space-y-4 text-left'
              }
            >
              {!project.proposals.length && (
                <div>
                  <MilestoneList
                    milestones={[]}
                    allDeliveryTimes={[]}
                    isConsultant={account === project.coordinator}
                  />
                </div>
              )}
              {project.proposals.map((proposal) => (
                <ProposalDetails
                  key={proposal.proposalId}
                  proposal={proposal}
                  project={project}
                  milestoneActions={
                    proposal.status === 'Approved'
                      ? (milestone) => (
                          <div className="mt-3 pl-6">
                            <OpenDisputeAction
                              project={project}
                              milestone={milestone}
                              disabled={
                                command.busy ||
                                command.pending ||
                                command.blocked
                              }
                            />
                            {((account === project.client &&
                              milestone.status === 'CompletionRequested') ||
                              ((account === project.client ||
                                account === project.coordinator) &&
                                ['ChangesRequested', 'Disputed'].includes(
                                  milestone.status ?? ''
                                ))) && (
                              <Link
                                className="text-primary underline"
                                to={
                                  '/projects/' +
                                  project.projectId +
                                  '/milestones/' +
                                  milestone.milestoneId +
                                  '/review'
                                }
                              >
                                {milestone.status === 'CompletionRequested'
                                  ? 'Review Milestone'
                                  : 'Review comments'}
                              </Link>
                            )}
                            {account === project.coordinator && (
                              <SubmitWorkAction
                                project={project}
                                milestone={milestone}
                                command={command}
                                canSubmit={
                                  !project.cancelled &&
                                  !project.completed &&
                                  !project.activeDisputeId &&
                                  !project.planning.frozen &&
                                  !milestone.frozen &&
                                  ['InProgress', 'ChangesRequested'].includes(
                                    milestone.status ?? ''
                                  )
                                }
                              />
                            )}
                          </div>
                        )
                      : undefined
                  }
                  actions={
                    reviewMode && account === project.client ? (
                      <ProposalReviewActions
                        project={project}
                        proposal={proposal}
                        command={command}
                      />
                    ) : null
                  }
                />
              ))}
            </section>
          </div>
          <ProjectTeamList project={project} />
          {project.proposals
            .filter((p) => p.status === 'Approved')
            .flatMap((p) => p.milestones)
            .map((milestone) => (
              <details
                key={milestone.milestoneId}
                className="rounded-[--radi-6] border border-[--base-border] p-4 text-left"
              >
                <summary className="cursor-pointer text-sm font-medium">
                  {milestone.definition.title} · {milestone.status} · Tasks
                  &amp; delivery
                </summary>
                <div className="mt-4 space-y-4">
                  <MilestoneTasks
                    milestone={milestone}
                    project={project}
                    command={command}
                    editable
                  />
                  <MilestoneCompletion
                    project={project}
                    milestone={milestone}
                    command={command}
                  />
                </div>
              </details>
            ))}

          {(account === project.client || account === project.coordinator) &&
            project.proposals.map((proposal) => (
              <details
                key={proposal.proposalId}
                open={
                  proposal.status === 'Draft' ||
                  proposal.status === 'PendingApproval'
                }
                className="text-left"
              >
                <summary className="cursor-pointer text-sm font-medium">
                  Scope review history
                </summary>
                <ProposalReviewHistory
                  projectId={project.projectId}
                  proposal={proposal}
                  allowReply={
                    proposal.status === 'Draft' ||
                    proposal.status === 'PendingApproval'
                      ? !project.cancelled &&
                        !project.completed &&
                        !project.planning.frozen &&
                        !project.activeDisputeId
                      : false
                  }
                />
              </details>
            ))}
          {!reviewMode &&
            (project.planning.status === 'AwaitingQuote' ||
              project.planning.status === 'Quoted') && (
              <PlanningControls project={project} command={command} />
            )}
        </div>
      ) : tab === 'tasks' ? (
        selectedMilestone ? (
          <section className="space-y-6 text-left">
            <Button variant="outline" onClick={() => setMilestone(null)}>
              Back to milestones
            </Button>
            <h2 className="text-xl font-semibold">
              {selectedMilestone.definition.title}
            </h2>
            <MilestoneTasks
              milestone={selectedMilestone}
              project={project}
              command={command}
              editable
            />
            <MilestoneCompletion
              project={project}
              milestone={selectedMilestone}
              command={command}
            />
          </section>
        ) : (
          <TasksKanbanView
            projects={[project]}
            onNavigate={() => undefined}
            onMilestoneClick={(milestone) =>
              setMilestone(milestone.milestoneId)
            }
          />
        )
      ) : (
        <section className="space-y-4 text-left">
          <ProjectPaymentSummary project={project} />
          <Link
            className="text-primary underline"
            to={'/payments/' + project.projectId + '/fund'}
          >
            Deposits and withdrawals
          </Link>
        </section>
      )}
    </MainScreen>
  );
}
function ProposalDetails({
  proposal,
  actions,
  project,
  milestoneActions,
}: {
  proposal: ProposalView;
  project: ProjectView;
  actions?: React.ReactNode;
  milestoneActions?: (milestone: MilestoneView) => React.ReactNode;
}) {
  const participants = useProjectParticipants(project.projectId);
  const presentation = useProposalPresentation(
    project.projectId,
    proposal.proposalId
  );
  return (
    <section className="space-y-10">
      {presentation.isError && (
        <div role="alert">
          <p>{presentation.error.message}</p>
          <Button onClick={() => void presentation.refetch()}>
            Retry milestone details
          </Button>
        </div>
      )}
      <ProposalMilestones
        delivery={proposal.delivery}
        definition={{
          title: proposal.title,
          description: proposal.description,
          milestones: proposal.milestones.map((m) => m.definition),
        }}
        presentation={presentation.data}
        milestones={proposal.milestones}
        title="Milestones"
        workers={participants.data?.workers}
        milestoneActions={milestoneActions}
      />
      {actions}
    </section>
  );
}
